<script>
	import { onMount } from "svelte";
	import { fade, fly } from "svelte/transition";

	export const prerender = true;

	let showContent = false;
	let logLines = [];
	const logs = [
		"> System check initiated...",
		"> Analyzing requirements matrix...",
		"> Loading architectural patterns...",
		"> AI Core: ONLINE",
		"> Ready for input.",
	];

	onMount(() => {
		showContent = true;
		logs.forEach((log, i) => {
			setTimeout(() => {
				logLines = [...logLines, log];
			}, i * 600);
		});
	});

	const steps = [
		{
			id: "01",
			title: "DEFINE",
			subtitle: "Operating Model",
			icon: "M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2",
		},
		{
			id: "02",
			title: "ARCHITECT",
			subtitle: "System Blueprint",
			icon: "M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10",
		},
		{
			id: "03",
			title: "DEPLOY",
			subtitle: "Production Ready",
			icon: "M13 10V3L4 14h7v7l9-11h-7z",
		},
	];
</script>

<svelte:head>
	<title>Initialize Build | OS Studio</title>
</svelte:head>

<section
	class="relative min-h-screen bg-[#05050a] text-white flex flex-col items-center justify-center overflow-hidden font-mono selection:bg-primary/30 selection:text-primary-300"
>
	<!-- Grid Background -->
	<div
		class="absolute inset-0 bg-[linear-gradient(rgba(0,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(0,255,255,0.03)_1px,transparent_1px)] bg-[size:50px_50px] [mask-image:radial-gradient(ellipse_at_center,black_40%,transparent_90%)] z-0"
	></div>

	<!-- Ambient Glows -->
	<div
		class="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/10 blur-[150px] rounded-full animate-pulse-glow z-0"
	></div>
	<div
		class="absolute bottom-1/4 right-1/4 w-96 h-96 bg-neon-cyan/5 blur-[150px] rounded-full animate-blob z-0"
	></div>

	<div
		class="relative z-10 w-full max-w-6xl px-6 grid lg:grid-cols-2 gap-16 items-center"
	>
		<!-- Left: System Status & Header -->
		<div class="space-y-12">
			<!-- Header -->
			<div class="space-y-4">
				{#if showContent}
					<div
						in:fly={{ y: 20, duration: 800, delay: 200 }}
						class="inline-flex items-center gap-2 px-3 py-1 bg-white/5 border border-white/10 rounded-full text-xs text-primary-300 uppercase tracking-widest"
					>
						<span
							class="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse"
						></span>
						System Online
					</div>
					<h1
						in:fly={{ y: 20, duration: 800, delay: 400 }}
						class="text-5xl md:text-7xl font-bold tracking-tighter leading-none"
					>
						BUILD <br />
						<span
							class="text-transparent bg-clip-text bg-gradient-to-r from-primary via-white to-neon-cyan"
							>SEQUENCE</span
						>
					</h1>
					<p
						in:fly={{ y: 20, duration: 800, delay: 600 }}
						class="text-gray-400 text-lg max-w-md border-l-2 border-primary/30 pl-4"
					>
						Initiate the autonomous construction of your operating
						platform. From abstract intent to concrete
						infrastructure.
					</p>
				{/if}
			</div>

			<!-- Terminal -->
			<div
				class="w-full max-w-md bg-black/40 border border-white/10 rounded-lg p-5 font-mono text-xs md:text-sm h-48 flex flex-col justify-end shadow-2xl backdrop-blur-sm"
			>
				<div class="space-y-1 text-green-400/80">
					{#each logLines as line}
						<p class="typing-effect">{line}</p>
					{/each}
					<span
						class="inline-block w-2 h-4 bg-green-500/50 animate-pulse ml-1"
					></span>
				</div>
			</div>
		</div>

		<!-- Right: Interactive Core & Options -->
		<div class="relative flex flex-col items-center justify-center">
			<!-- Center Core Animation -->
			<div
				class="relative w-64 h-64 md:w-80 md:h-80 mb-12 flex items-center justify-center"
			>
				<!-- Outer Rings -->
				<div
					class="absolute inset-0 rounded-full border border-primary/20 animate-[spin_10s_linear_infinite]"
				></div>
				<div
					class="absolute inset-4 rounded-full border border-dashed border-neon-cyan/20 animate-[spin_15s_linear_infinite_reverse]"
				></div>
				<div
					class="absolute inset-12 rounded-full border border-white/10 animate-pulse"
				></div>

				<!-- Core Glow -->
				<div
					class="absolute inset-0 bg-primary/20 blur-3xl rounded-full animate-pulse"
				></div>

				<!-- Center Logo/Icon -->
				<div
					class="relative z-10 w-32 h-32 bg-[#0a0a0f] rounded-full border border-primary/50 flex items-center justify-center shadow-[0_0_50px_rgba(124,58,237,0.3)]"
				>
					<svg
						class="w-16 h-16 text-white"
						fill="none"
						viewBox="0 0 24 24"
						stroke="currentColor"
					>
						<path
							stroke-linecap="round"
							stroke-linejoin="round"
							stroke-width="1.5"
							d="M19.428 15.428a2 2 0 00-1.022-.547l-2.384-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z"
						/>
					</svg>
				</div>

				<!-- Floating Nodes -->
				{#each steps as step, i}
					<div
						class="absolute w-12 h-12 bg-[#13131f] border border-white/20 rounded-xl flex items-center justify-center text-primary shadow-lg backdrop-blur-md transition-all duration-500 hover:scale-125 hover:border-primary cursor-pointer hover:shadow-[0_0_20px_rgba(124,58,237,0.5)] z-20"
						style={`top: ${50 + 40 * Math.sin(i * ((2 * Math.PI) / 3) - Math.PI / 2)}%; left: ${50 + 40 * Math.cos(i * ((2 * Math.PI) / 3) - Math.PI / 2)}%; transform: translate(-50%, -50%);`}
					>
						<span class="sr-only">{step.title}</span>
						<svg
							class="w-5 h-5"
							fill="none"
							viewBox="0 0 24 24"
							stroke="currentColor"
						>
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="1.5"
								d={step.icon}
							/>
						</svg>

						<!-- Label -->
						<div
							class="absolute top-full mt-2 left-1/2 -translate-x-1/2 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity"
						>
							<span
								class="text-[10px] text-white bg-black/80 px-2 py-1 rounded border border-white/10 uppercase tracking-widest"
								>{step.title}</span
							>
						</div>
					</div>
				{/each}
			</div>

			<!-- Initialize Button -->
			<a
				href="/register?plan=professional"
				class="group relative inline-flex items-center justify-center px-8 py-4 bg-transparent overflow-hidden rounded-xl transition-all hover:scale-105"
			>
				<div
					class="absolute inset-0 w-full h-full bg-gradient-to-r from-primary via-neon-cyan to-primary opacity-20 group-hover:opacity-100 blur-md transition-opacity duration-500"
				></div>
				<div
					class="absolute inset-[1px] bg-[#0a0a0f] rounded-xl z-0"
				></div>
				<span
					class="relative z-10 text-lg font-bold text-white tracking-widest uppercase flex items-center gap-3 group-hover:text-white transition-colors"
				>
					Initialize Sequence
					<svg
						class="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1"
						fill="none"
						viewBox="0 0 24 24"
						stroke="currentColor"
					>
						<path
							stroke-linecap="round"
							stroke-linejoin="round"
							stroke-width="2"
							d="M14 5l7 7m0 0l-7 7m7-7H3"
						/>
					</svg>
				</span>
			</a>

			<p class="mt-6 text-xs text-gray-500 uppercase tracking-widest">
				Version 2.4.0-RC1 // Ready
			</p>
		</div>
	</div>
</section>

<style>
	/* Custom typewriter animation handled by JS/Svelte logic roughly, 
	   but basic blink is CSS */
	.typing-effect {
		animation: fadeIn 0.3s ease-out forwards;
	}

	@keyframes fadeIn {
		from {
			opacity: 0;
			transform: translateY(5px);
		}
		to {
			opacity: 1;
			transform: translateY(0);
		}
	}
</style>
